import { Map } from 'yandex-maps';
import { getBoundsCenter } from '@lib/map-helpers';
import { SleeveManager } from '@lib/sleeve-manager';
import { getBagPlace } from '@lib/bag-formatting';
import { createWhMarker } from './wh-placemark';
import {
  $agricultureById,
  $agricultureIsLoading,
} from '@@/agriculture/store/agriculture.store';
import { IAgriculture } from '@@/agriculture/ts';
import {
  $zoomModeEnabled,
  onContextMenu,
  selectSleeve,
} from '@widgets/storage-viewer/model';
import type { SleeveDescription } from '@lib/sleeve-manager';
import { IWarehouse, IWarehouseWithBags, WarehouseTypes } from '@@/warehouses/ts';
import { DiContainer } from '@@/di/di-container';
import { ClientStorageService } from '@@/common/services/client-storage.service';

import styles from '@@/agriculture/components/agriculture-hint-paramaters/index.module.sass';

const DEFAULT_VIEWER_SETTINGS = {
  center: [45.04484, 38.97603],
  zoom: 8,
  zoom_default: 8,
  controls: ['zoomControl', 'geolocationControl', 'typeSelector', 'fullscreenControl'],
};

const clientStorageService = DiContainer.get(ClientStorageService);

const VIEWER_SETTINGS = 'VIEWER_SETTINGS';

class WarehouseMap {
  private _map?: Map;
  private _container?: HTMLElement;
  private _containerId?: string;
  private _markers: { [whId: number | string]: ymaps.Placemark } = {};
  private _viewerSettings: typeof DEFAULT_VIEWER_SETTINGS = DEFAULT_VIEWER_SETTINGS;
  private _savedSettings: {
    type: ymaps.MapType;
    zoom: number;
    position: number[];
  } | null = null;
  private _sleeveManager: SleeveManager<SleeveDescription[]> | null = null;
  private _sleeves: ymaps.Placemark[] = [];

  private initViewerSettings() {
    try {
      const storedViewerSettings = clientStorageService.get<string>(VIEWER_SETTINGS);

      this._viewerSettings = storedViewerSettings
        ? JSON.parse(storedViewerSettings)
        : DEFAULT_VIEWER_SETTINGS;
      clientStorageService.set(VIEWER_SETTINGS, this._viewerSettings);
    } catch (error) {
      this._viewerSettings = DEFAULT_VIEWER_SETTINGS;
    }
    if (!$zoomModeEnabled.getState() && this._viewerSettings.zoom_default) {
      this._viewerSettings.zoom = this._viewerSettings.zoom_default;
    }
  }

  private updateViewerSettings = () => {
    const center = this._map?.getCenter() ?? DEFAULT_VIEWER_SETTINGS.center;
    const zoom = this._map?.getZoom() ?? DEFAULT_VIEWER_SETTINGS.zoom;
    const settings = { ...this._viewerSettings, center, zoom };
    if (!$zoomModeEnabled.getState()) {
      settings.zoom_default = zoom;
    }
    clientStorageService.set(VIEWER_SETTINGS, settings);
  };

  init(id: string, container: HTMLElement | null) {
    if (!container) throw new Error('Не задан container');

    this._container = container;
    this._containerId = id;

    this.initViewerSettings();

    const map = new ymaps.Map(this._containerId, this._viewerSettings);

    this._map = map;

    map.events.add('actionend', this.updateViewerSettings);

    map.behaviors.disable('dblClickZoom');
  }

  dispose() {
    if (this._map) {
      console.log('Удаляем карту ...');
      this._map.destroy();
    }

    if (this._sleeveManager) this._sleeveManager.dispose();
    this._markers = {};
  }

  resize() {
    if (this._map) this._map.container.fitToViewport();
  }

  private buildSleeves(wh: IWarehouseWithBags) {
    const agricultureIsLoading = $agricultureIsLoading.getState();
    const agricultureById = $agricultureById.getState();

    if (!this._sleeveManager || agricultureIsLoading) return;

    this._sleeves.forEach((s) => this._map?.geoObjects.remove(s));
    this._sleeves = [];

    const descriptions: SleeveDescription[] = wh.bags.map((bag) => {
      const culture = agricultureById[bag.agricultureId as number] as
        | IAgriculture
        | undefined;

      const color = culture ? culture.color : '#ffffff';
      const params = bag.values.map(({ name, value, units }) => ({
        label: name,
        value: `${value}${units}`,
      }));

      return {
        params,
        id: bag.id,
        label: getBagPlace(bag.id).toString(),
        position: bag.position,
        color: color,
        strokeColor: color,
        hintClassName: styles.root,
        opacity: bag.agricultureId ? 1 : 0.4,
        events: {
          click: ({ id }) => selectSleeve(id.toString()),
          context: (sleeve, e) => {
            let id = sleeve.id.toString();
            let coords = e.get('pagePixels');
            // console.log('context menu');
            setTimeout(() => {
              // TODO: убрать таймаут. и разобраться почему пустое меню вылазит если открыть контекстное меню когда оно уже открыто на другом типе рукава
              onContextMenu({
                coords,
                id,
              });
            }, 200);
          },
        },
      };
    });

    const sleeves = this._sleeveManager.buildSleevesByStartEndPoints(descriptions);

    sleeves.forEach((s) => {
      this._sleeves.push(s.marker);
      this._map?.geoObjects.add(s.marker);
    });
  }

  /**
   * updateZoomPlacemarks
   */
  public updateZoomPlacemarks(wh: IWarehouseWithBags) {
    if (this._map && this._sleeveManager) this.buildSleeves(wh);
  }

  /**
   * enterZoomMode
   */
  public enterZoomMode(wh: IWarehouseWithBags) {
    if (!this._map) return;

    this._sleeves = [];
    this._map.geoObjects.removeAll();
    this._savedSettings = {
      type: this._map.getType(),
      zoom: this._map.getZoom(),
      position: this._map.getCenter(),
    };

    const field = new ymaps.Polygon(
      [wh.position],
      {},
      {
        strokeColor: '#FFF1B8',
        fill: false,
        strokeWidth: 2,
        strokeStyle: 'solid',
        interactivityModel: 'default#transparent',
      },
    );

    this._map.geoObjects.add(field);

    const bounds = field.geometry?.getBounds() as number[][];
    const center = getBoundsCenter(bounds);

    this._map.setBounds(bounds);
    this._map.setCenter(center);
    this._map.setType('yandex#satellite');

    this._sleeveManager = new SleeveManager(this._map);
    this._sleeveManager.sleeveLength = wh.bagsSize;

    this.buildSleeves(wh);
  }

  /**
   * leaveZoomMode
   */
  public leaveZoomMode() {
    if (!this._map) return;

    this._map.geoObjects.removeAll();
    this._sleeveManager?.dispose();

    const allIds = Object.keys(this._markers);

    for (const id of allIds) {
      this._map.geoObjects.add(this._markers[id]);
    }

    if (this._savedSettings) {
      this._map.setZoom(this._savedSettings.zoom);
      this._map.setType(this._savedSettings.type);
      this._map.setCenter(this._savedSettings.position);
    }
  }

  /**
   * moveToWarehouse
   *
   * @description Перемещает камеру на маркер выбранного склада
   */
  public moveToWarehouse(warehouseId: number) {
    if (!this._map || !this._markers) return;

    const warehouseMarker = this._markers[warehouseId];

    if (!warehouseMarker) {
      throw new Error('Маркера для этого склада не существует');
    }

    const newCenter =
      warehouseMarker.geometry?.getCoordinates() ?? DEFAULT_VIEWER_SETTINGS.center;

    this._map.setCenter(newCenter, this._map.getZoom(), { duration: 300 });
  }

  // getContextMenu(warehouse: warehouseModel.Warehouse) {}

  /**
   * addWarehouse
   *
   * @description Добавляет метку склада на карту
   */
  public addWarehouse(
    warehouse: IWarehouse,
    addToGeoObjects: boolean = true,
    onClick?: () => void,
    onDblClick?: () => void,
    onContextMenu?: (coords: Array<number>) => void,
  ) {
    if (!this._map || !warehouse.marker) return;

    const placeMark = createWhMarker(warehouse);

    this._markers[warehouse.id] = placeMark;

    if (onClick) {
      placeMark.events.add('click', onClick);
    }
    if (onDblClick && warehouse.type == 2) placeMark.events.add('dblclick', onDblClick);
    if (onContextMenu)
      placeMark.events.add('contextmenu', (e) => {
        onContextMenu(e.get('pagePixels'));
      });
    if (addToGeoObjects) this._map.geoObjects.add(placeMark);
  }

  /**
   * removeWarehouse
   *
   */
  public removeWarehouse(warehouseId: number) {
    if (!this._map) {
      return;
    }

    if (this._markers[warehouseId]) {
      this._map.geoObjects.remove(this._markers[warehouseId]);
      delete this._markers[warehouseId];
    }
  }

  /**
   * clearMap
   */
  public clear() {
    if (this._map) {
      this._markers = {};
      this._sleeves = [];
      this._map.geoObjects.removeAll();
    }
  }
}

export const map = new WarehouseMap();
